/**
 * Shift Comparison Page (Phase 25.3)
 *
 * Manager-facing page that answers "why does D-shift run better than A-shift?".
 * Renders horizontal shift cards with:
 *  - Score badge (score-colors.ts)
 *  - Inline SVG sparkline of daily avg score
 *  - Metric tiles (overall / compliance / efficiency / safety / assessments)
 *  - Delta vs peer average
 *  - "Why the delta?" top-3 drivers (sopSections with highest cross-shift spread)
 *
 * Filters: date range (7d / 30d / 90d / custom), SOP, Zone.
 * Click a card -> /analytics/shift-comparison/:shiftId (preserves filters).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  GitCompare,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  ChevronRight,
  Filter,
  Calendar,
} from 'lucide-react';
import { useShiftComparison, type ShiftCard, type SparklinePoint, type TopDriver } from '../../hooks/useShiftComparison';
import { useSopLookup } from '../../hooks/useSOPs';
import { useStationLookup } from '../../hooks/useStations';
import { getScoreColor, getScoreBadgeColor } from '../../lib/score-colors';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { cn } from '../../lib/utils';

type Preset = '7d' | '30d' | '90d' | 'custom';

const PRESETS: { value: Preset; label: string; days: number | null }[] = [
  { value: '7d', label: '7 Days', days: 7 },
  { value: '30d', label: '30 Days', days: 30 },
  { value: '90d', label: '90 Days', days: 90 },
  { value: 'custom', label: 'Custom', days: null },
];

function computeRangeFromPreset(preset: Preset): {
  startDate: string;
  endDate: string;
} {
  const end = new Date();
  const start = new Date();
  const days = PRESETS.find((p) => p.value === preset)?.days ?? 30;
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export function ShiftComparisonPage() {
  useDocumentTitle('Shift Comparison');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---------- URL-driven state ----------
  const initialPreset = (searchParams.get('preset') as Preset) ?? '30d';
  const initialSopId = searchParams.get('sopId') ?? '';
  const initialStationId = searchParams.get('stationId') ?? '';
  const initialStartDate = searchParams.get('startDate') ?? '';
  const initialEndDate = searchParams.get('endDate') ?? '';

  const [preset, setPreset] = useState<Preset>(initialPreset);
  const [sopId, setSopId] = useState(initialSopId);
  const [stationId, setStationId] = useState(initialStationId);
  const [customStart, setCustomStart] = useState(initialStartDate);
  const [customEnd, setCustomEnd] = useState(initialEndDate);

  // ---------- Derived date range ----------
  const dateRange = useMemo(() => {
    if (preset === 'custom' && customStart && customEnd) {
      return {
        startDate: new Date(customStart).toISOString(),
        endDate: new Date(customEnd).toISOString(),
      };
    }
    return computeRangeFromPreset(preset === 'custom' ? '30d' : preset);
  }, [preset, customStart, customEnd]);

  // ---------- Lookups ----------
  const { data: sopLookup } = useSopLookup();
  const { data: stationLookup } = useStationLookup();

  // ---------- Data ----------
  const { data, isLoading } = useShiftComparison({
    sopId: sopId || undefined,
    stationId: stationId || undefined,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  // ---------- Sync filters to URL ----------
  const applyFilters = () => {
    const next = new URLSearchParams();
    next.set('preset', preset);
    if (sopId) next.set('sopId', sopId);
    if (stationId) next.set('stationId', stationId);
    if (preset === 'custom') {
      if (customStart) next.set('startDate', customStart);
      if (customEnd) next.set('endDate', customEnd);
    }
    setSearchParams(next, { replace: true });
  };

  const handleCardClick = (shift: ShiftCard) => {
    const next = new URLSearchParams();
    next.set('preset', preset);
    if (sopId) next.set('sopId', sopId);
    if (stationId) next.set('stationId', stationId);
    if (preset === 'custom') {
      if (customStart) next.set('startDate', customStart);
      if (customEnd) next.set('endDate', customEnd);
    }
    navigate(`/analytics/shift-comparison/${shift.shiftId}?${next.toString()}`);
  };

  const shifts = data?.comparison ?? [];
  const topDrivers = data?.topDrivers ?? [];
  const summary = data?.summary;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-green/10 text-guild-green">
            <GitCompare size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Shift Comparison</h1>
            <p className="text-sm text-gray-500">
              Compare overall performance across shifts — score, trends, drivers
            </p>
          </div>
        </div>
        {summary && (
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-gray-400">Org average</p>
            <p
              className={cn(
                'text-2xl font-bold tabular-nums',
                getScoreColor(summary.overallAverage, 'text')
              )}
            >
              {summary.overallAverage != null ? summary.overallAverage.toFixed(1) : '--'}
            </p>
            <p className="text-xs text-gray-500">
              {summary.totalAssessments} assessments · {summary.totalOperators} operators
            </p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <Filter size={16} />
            Filters
          </div>

          {/* Date preset */}
          <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
            {PRESETS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPreset(p.value)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200',
                  preset === p.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom date inputs */}
          {preset === 'custom' && (
            <>
              <div className="flex items-center gap-1.5">
                <Calendar size={14} className="text-gray-400" />
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green"
                />
              </div>
              <span className="text-gray-400">→</span>
              <div className="flex items-center gap-1.5">
                <Calendar size={14} className="text-gray-400" />
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green"
                />
              </div>
            </>
          )}

          {/* SOP filter */}
          <select
            value={sopId}
            onChange={(e) => setSopId(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green"
          >
            <option value="">All SOPs</option>
            {sopLookup?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>

          {/* Zone (stations) filter */}
          <select
            value={stationId}
            onChange={(e) => setStationId(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green"
          >
            <option value="">All Zones</option>
            {stationLookup?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <button
            onClick={applyFilters}
            className="ml-auto rounded-lg bg-guild-green px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-gray-200 bg-white">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : shifts.length === 0 ? (
        <EmptyState title="No shifts to compare">
          Create shifts and assign operators to see comparisons here.
        </EmptyState>
      ) : summary?.totalAssessments === 0 ? (
        // G-29: the page reads SOP assessments only. With none to compare every
        // shift card is blank, so explain that and point to KPI Analytics.
        <EmptyState title="No SOP assessments to compare">
          This page compares SOP assessments by operators assigned to a shift, and none
          match this range and these filters. For KPI results by shift, open{' '}
          <Link to="/analytics/kpi" className="font-medium text-guild-green hover:underline">
            KPI Analytics
          </Link>
          , pick a KPI and group by Shift.
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4">
            {shifts.map((shift) => (
              <ShiftComparisonCard
                key={shift.shiftId}
                shift={shift}
                onClick={() => handleCardClick(shift)}
                hasPeers={(summary?.shiftsWithData ?? 0) > 1}
              />
            ))}
          </div>

          <TopDriversPanel drivers={topDrivers} />
        </>
      )}
    </div>
  );
}

// ============================================
// SHIFT CARD
// ============================================

function ShiftComparisonCard({
  shift,
  onClick,
  hasPeers,
}: {
  shift: ShiftCard;
  onClick: () => void;
  hasPeers: boolean;
}) {
  const scoreBadge = getScoreBadgeColor(shift.avgScore);
  const hasData = shift.avgScore != null;

  return (
    <button
      onClick={onClick}
      className="group flex w-full flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left transition-all hover:border-guild-green/50 hover:shadow-md lg:flex-row lg:items-center"
    >
      {/* Left: rank + name + score */}
      <div className="flex items-center gap-4 lg:min-w-[260px]">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-50 text-base font-bold text-gray-600">
          #{shift.rank}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-gray-900">
            {shift.shiftName}
          </p>
          <p className="text-xs text-gray-500">
            {shift.shiftCode ?? '—'} · {shift.operatorCount} operators
          </p>
        </div>
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border px-4 py-2 min-w-[92px]',
            scoreBadge
          )}
        >
          <span className="text-2xl font-bold tabular-nums">
            {hasData ? shift.avgScore!.toFixed(1) : '--'}
          </span>
          <span className="text-[10px] font-medium uppercase tracking-wide opacity-75">
            Overall
          </span>
        </div>
      </div>

      {/* Middle: sparkline */}
      <div className="flex-1 min-w-[160px]">
        <Sparkline points={shift.sparkline} />
      </div>

      {/* Right: metrics */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 lg:min-w-[360px]">
        <MetricTile label="Compliance" value={shift.avgCompliance} />
        <MetricTile label="Efficiency" value={shift.avgEfficiency} />
        <MetricTile label="Safety" value={shift.avgSafety} />
        <MetricTile
          label="Assessments"
          valueOverride={shift.assessmentCount.toString()}
        />
      </div>

      {/* Delta */}
      <div className="flex items-center gap-2 lg:min-w-[140px] lg:justify-end">
        {hasPeers ? (
          <DeltaBadge value={shift.deltaVsPeers} />
        ) : (
          <span className="text-xs text-gray-400">No peer shifts</span>
        )}
        <ChevronRight
          size={18}
          className="text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-guild-green"
        />
      </div>
    </button>
  );
}

function MetricTile({
  label,
  value,
  valueOverride,
}: {
  label: string;
  value?: number | null;
  valueOverride?: string;
}) {
  const display =
    valueOverride ?? (value != null ? value.toFixed(1) : '--');
  const textClass =
    valueOverride !== undefined
      ? 'text-gray-900'
      : getScoreColor(value ?? null, 'text');
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className={cn('text-sm font-bold tabular-nums', textClass)}>
        {display}
      </p>
    </div>
  );
}

function DeltaBadge({ value }: { value: number | null }) {
  if (value == null) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const isPositive = value > 0.5;
  const isNegative = value < -0.5;
  const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
  const tone = isPositive
    ? 'bg-emerald-50 text-emerald-700'
    : isNegative
      ? 'bg-red-50 text-red-700'
      : 'bg-gray-100 text-gray-600';
  const sign = value > 0 ? '+' : '';
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums',
        tone
      )}
    >
      <Icon size={12} />
      {sign}
      {value.toFixed(1)}
      <span className="ml-1 text-[10px] font-medium opacity-75">vs peers</span>
    </div>
  );
}

// ============================================
// SPARKLINE (inline SVG)
// ============================================

function Sparkline({ points }: { points: SparklinePoint[] }) {
  const withScore = points.filter((p) => p.avgScore != null);
  if (withScore.length < 2) {
    return (
      <div className="flex h-10 items-center justify-center rounded-md bg-gray-50 text-[10px] text-gray-400">
        {withScore.length === 1 ? 'Single data point' : 'No data in range'}
      </div>
    );
  }

  const width = 220;
  const height = 40;
  const padding = 2;
  const min = Math.min(...withScore.map((p) => p.avgScore!));
  const max = Math.max(...withScore.map((p) => p.avgScore!));
  const range = Math.max(max - min, 1);

  const coords = withScore.map((p, i) => {
    const x = padding + (i / (withScore.length - 1)) * (width - padding * 2);
    const y =
      height -
      padding -
      ((p.avgScore! - min) / range) * (height - padding * 2);
    return { x, y };
  });

  const path = coords
    .map((c, i) => (i === 0 ? `M ${c.x} ${c.y}` : `L ${c.x} ${c.y}`))
    .join(' ');

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="block"
    >
      <path d={path} fill="none" stroke="#00674a" strokeWidth={1.75} />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={1.5} fill="#00674a" />
      ))}
    </svg>
  );
}

// ============================================
// TOP DRIVERS
// ============================================

function TopDriversPanel({ drivers }: { drivers: TopDriver[] }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-6 py-4">
        <h3 className="font-semibold text-gray-900">Why the delta?</h3>
        <p className="text-sm text-gray-500">
          Jobs (SOP sections) with the largest score gap across shifts
        </p>
      </div>
      <div className="p-6">
        {drivers.length === 0 ? (
          <p className="text-sm text-gray-500">
            Not enough cross-shift data to identify drivers yet.
          </p>
        ) : (
          <ol className="space-y-3">
            {drivers.map((d, idx) => (
              <li
                key={d.sectionId}
                className="flex items-center justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-guild-mint text-xs font-bold text-guild-green">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {d.sectionTitle}
                    </p>
                    <p className="text-xs text-gray-500">
                      Low: {d.minScore.toFixed(1)} · High: {d.maxScore.toFixed(1)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Spread
                  </p>
                  <p className="text-lg font-bold tabular-nums text-amber-600">
                    {d.shiftSpread.toFixed(1)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================

function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-20">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
        <Users size={32} className="text-gray-400" />
      </div>
      <p className="mt-4 text-sm font-medium text-gray-900">{title}</p>
      <p className="mt-1 max-w-md text-center text-sm text-gray-500">{children}</p>
    </div>
  );
}

export default ShiftComparisonPage;
